import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  OnChanges,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FormOption } from '../../../shared/models/form-option.interface';
import { SelectOptionComponent } from '../select-option/select-option.component';

@Component({
  selector: 'app-form-select-dropdown',
  templateUrl: './select-dropdown.component.html',
  styleUrls: ['./select-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, NgClass, NgStyle, SelectOptionComponent],
  standalone: true,
})
export class SelectDropdownComponent implements OnChanges {
  readonly disabled = input<boolean>();
  readonly isRelative = input<boolean>();
  readonly expanded = input<boolean | null>();
  readonly noResultsLabel = input<string>();
  readonly options = input<(FormOption[] | null) | undefined>([]);
  readonly optionSubLabels = input<string[] | undefined>([]);
  readonly optionTemplate = input<TemplateRef<unknown>>();
  readonly searchTerm = input<string | null>();
  readonly selectedValue = input<string | null>();
  readonly selectedValues = input<string[]>();
  @ViewChildren('option') optionComponents?: QueryList<SelectOptionComponent>;

  @Output() collapsed: EventEmitter<void> = new EventEmitter<void>();
  @Output() selected: EventEmitter<FormOption> = new EventEmitter<FormOption>();

  focusedOption$: BehaviorSubject<SelectOptionComponent | undefined> =
    new BehaviorSubject<SelectOptionComponent | undefined>(undefined);

  private readonly elementRef = inject(ElementRef);

  ngOnChanges(): void {
    const expanded = this.expanded();
    if (expanded && this.options()?.length) {
      this.focusOnSelectedOption();
    } else if (!expanded) {
      this.focusedOption$.next(undefined);
    }
  }

  trackOption(index: number, option: FormOption): string {
    return option.value;
  }

  onOptionSelection(option: FormOption): void {
    this.selected.emit(option);
  }

  @HostListener('document:keydown', ['$event'])
  onSelectDropdownKeydown = (event: KeyboardEvent): void => {
    if (!this.expanded()) {
      return;
    }

    switch (event.key) {
      case 'Escape':
      case 'Tab':
        this.onCollapse();
        return;
      case 'Home':
        event.preventDefault();
        this.focusOnOption(0);
        return;
      case 'End':
        event.preventDefault();
        this.focusOnOption(this.optionComponents?.length ?? -1);
        return;
      case 'ArrowDown':
        event.preventDefault();
        this.focusOnOption(this.focusedOptionIndex + 1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.focusOnOption(this.focusedOptionIndex - 1);
        return;
      default:
        return;
    }
  };

  @HostListener('document:mousedown', ['$event'])
  onSelectDropdownClick = (event: MouseEvent): void => {
    if (
      this.expanded() &&
      !this.elementRef.nativeElement.parentElement.contains(event.target)
    ) {
      this.onCollapse();
    }
  };

  private onCollapse(): void {
    this.collapsed.emit();
  }

  private focusOnOption(index: number): void {
    index = index < 0 ? 0 : index;
    index =
      this.optionComponents && index >= this.optionComponents.length
        ? this.optionComponents.length - 1
        : index;

    const optionComponent: SelectOptionComponent | undefined =
      this.optionComponents?.toArray()[index];

    this.focusedOption$.next(optionComponent ?? this.optionComponents?.first);
  }

  private focusOnSelectedOption(): void {
    const selectedOption = this.optionComponents
      ?.toArray()
      .find((option: SelectOptionComponent) => option.selected());

    this.focusedOption$.next(selectedOption ?? this.optionComponents?.first);
  }

  private get focusedOptionIndex(): number {
    return (
      this.optionComponents
        ?.toArray()
        .findIndex((option: SelectOptionComponent) => option.focused()) ?? 0
    );
  }
}
